import {
  Add as AddIcon,
  AutoAwesome as AutoAwesomeIcon,
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteOutlineIcon,
  EditOutlined as EditOutlinedIcon,
  HelpOutline as HelpOutlineIcon,
  Language as LanguageIcon,
  PauseCircleOutline as PauseCircleOutlineIcon,
  PlayCircleOutline as PlayCircleOutlineIcon,
  Public as PublicIcon,
  ShieldOutlined as ShieldIcon,
  Tag as TagIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import HttpServices from 'src/services/httpService';
import { postsModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import Spinner from 'src/utils/Loader/spinner';
import { useToaster } from 'src/utils/toaster/toasterContext';

const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const contentTypes = ['Text', 'Image', 'Carousel'];
const steps = ['Business', 'Prompts', 'Safety', 'Schedule', 'Preview Plan'];
const logoPositions = ['Top left', 'Top center', 'Top right', 'Bottom left', 'Bottom center', 'Bottom right'];

const emptyPrompt = { label: '', prompt: '' };
const emptyImageTheme = { label: '', prompt: '', theme_color: '' };
const emptyReferenceImage = { media_url: '', thumbnail_urls: [], description: '' };
const formatDay = (day) => day.charAt(0).toUpperCase() + day.slice(1);
const allowedUploadExtensions = ['jpg', 'jpeg', 'png', 'webp'];
const allowedUploadMimeTypes = ['image/jpeg', 'image/png', 'image/webp'];
const legacyAutoImageThemeLabels = [
  'Bedroom interior design',
  'Pooja unit interior design',
  'Kitchen interior design',
  'Living room interior design',
  'Primary service visual',
  'Customer outcome visual',
  'Product/service scene',
  'Brand visual',
];
const themeStopWords = new Set([
  'a',
  'an',
  'and',
  'are',
  'as',
  'at',
  'best',
  'business',
  'by',
  'company',
  'for',
  'from',
  'generate',
  'in',
  'i',
  'image',
  'images',
  'is',
  'near',
  'need',
  'my',
  'of',
  'on',
  'our',
  'premium',
  'the',
  'to',
  'top',
  'using',
  'we',
  'website',
  'with',
  'your',
]);

const defaultConfig = {
  website_url: '',
  business_summary: '',
  content_language: 'English',
  locations: [],
  negative_words: [],
  hashtags: [],
  content_prompts: [{ label: 'Content idea', prompt: '' }],
  image_prompts: [{ label: 'Image idea', prompt: '' }],
  image_themes: [],
  reference_images: [],
  logo_settings: { use_logo: false, media_url: '', thumbnail_urls: [], position: logoPositions[0] },
  schedule: [
    { day_of_week: 'monday', posts_per_day: 1, times: ['10:00'] },
    { day_of_week: 'wednesday', posts_per_day: 1, times: ['14:00'] },
    { day_of_week: 'friday', posts_per_day: 1, times: ['11:00'] },
  ],
  let_ai_choose_schedule: true,
  posts_per_day_default: 1,
  content_types: ['Text', 'Image'],
  include_hashtags: true,
  target_profile_ids: [],
  model: '',
  model_provider: '',
  is_active: true,
  is_setup_complete: false,
  auto_pilot_status: 'draft',
  generation_status: 'idle',
  last_run_message: '',
  scheduler_interval_hours: 12,
  last_run_at: null,
  next_run_at: null,
};

const asArray = (value, fallback = []) => (Array.isArray(value) ? value : fallback);

const clonePrompts = (prompts, fallback) =>
  asArray(prompts, fallback).map((item) => ({
    label: item?.label || '',
    prompt: item?.prompt || '',
  }));

const cloneImageThemes = (themes, fallback) =>
  asArray(themes, fallback).map((item) => ({
    label: item?.label || '',
    prompt: item?.prompt || '',
    theme_color: item?.theme_color || item?.themeColor || '',
  }));

const cloneReferenceImages = (images) =>
  asArray(images).map((item) => ({
    media_url: item?.media_url || '',
    thumbnail_urls: asArray(item?.thumbnail_urls),
    description: item?.description || '',
  }));

const normalizeLogoSettings = (settings = {}) => ({
  use_logo: Boolean(settings?.use_logo),
  media_url: settings?.media_url || '',
  thumbnail_urls: asArray(settings?.thumbnail_urls),
  position: settings?.position || logoPositions[0],
});

const normalizeSchedule = (schedule) =>
  asArray(schedule, defaultConfig.schedule)
    .map((item) => ({
      day_of_week: item?.day_of_week || '',
      posts_per_day: Number(item?.posts_per_day) || 1,
      times: asArray(item?.times),
    }))
    .filter((item) => item.day_of_week);

const normalizeConfig = (value = {}) => {
  const merged = { ...defaultConfig, ...(value || {}) };
  return {
    ...merged,
    content_language: merged.content_language || defaultConfig.content_language,
    locations: asArray(merged.locations),
    negative_words: asArray(merged.negative_words),
    hashtags: asArray(merged.hashtags),
    content_prompts: clonePrompts(merged.content_prompts, defaultConfig.content_prompts),
    image_prompts: clonePrompts(merged.image_prompts, defaultConfig.image_prompts),
    image_themes: cloneImageThemes(merged.image_themes, defaultConfig.image_themes),
    reference_images: cloneReferenceImages(merged.reference_images),
    logo_settings: normalizeLogoSettings(merged.logo_settings),
    schedule: normalizeSchedule(merged.schedule),
    content_types: asArray(merged.content_types, defaultConfig.content_types),
    target_profile_ids: asArray(merged.target_profile_ids),
    let_ai_choose_schedule:
      typeof merged.let_ai_choose_schedule === 'boolean'
        ? merged.let_ai_choose_schedule
        : defaultConfig.let_ai_choose_schedule,
    posts_per_day_default: Number(merged.posts_per_day_default) || defaultConfig.posts_per_day_default,
    include_hashtags: typeof merged.include_hashtags === 'boolean' ? merged.include_hashtags : defaultConfig.include_hashtags,
    auto_pilot_status: merged.auto_pilot_status || defaultConfig.auto_pilot_status,
    generation_status: merged.generation_status || defaultConfig.generation_status,
    last_run_message: merged.last_run_message || '',
    scheduler_interval_hours: Number(merged.scheduler_interval_hours) || defaultConfig.scheduler_interval_hours,
    last_run_at: merged.last_run_at || null,
    next_run_at: merged.next_run_at || null,
  };
};

const formatScheduleSummary = (schedule) => {
  const selectedSchedule = asArray(schedule);
  if (selectedSchedule.length === 0) return 'No days selected';

  return selectedSchedule
    .map((item) => {
      const postLabel = item.posts_per_day === 1 ? '1 post' : `${item.posts_per_day} posts`;
      const timeLabel = asArray(item.times).length > 0 ? ` at ${asArray(item.times).join(', ')}` : '';
      return `${formatDay(item.day_of_week)}: ${postLabel}${timeLabel}`;
    })
    .join(' | ');
};

const getTotalPosts = (config) =>
  config.let_ai_choose_schedule
    ? Number(config.posts_per_day_default || defaultConfig.posts_per_day_default) * 7
    : asArray(config.schedule).reduce((total, item) => total + (Number(item.posts_per_day) || 0), 0);

const getCompletedPromptCount = (prompts) => asArray(prompts).filter((item) => item?.label?.trim() && item?.prompt?.trim()).length;

const listValue = (values, fallback = 'None') => {
  const filteredValues = asArray(values).filter(Boolean);
  return filteredValues.length > 0 ? filteredValues.join(', ') : fallback;
};

const normalizeThemeColorInput = (value) => (/^#[0-9a-f]{6}$/i.test(value || '') ? value : '#2563eb');

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
};

const titleCase = (value) =>
  value
    .split(' ')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

const getWebsiteHost = (websiteUrl = '') => {
  try {
    return new URL(websiteUrl).hostname.replace(/^www\./, '');
  } catch (error) {
    return websiteUrl.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
  }
};

const getBusinessContext = (value) => {
  const summary = value?.business_summary?.trim();
  const host = getWebsiteHost(value?.website_url || '');
  const locations = asArray(value?.locations).join(', ');
  const contextParts = [summary, host ? `Website: ${host}` : '', locations ? `Locations: ${locations}` : ''];
  return contextParts.filter(Boolean).join(' | ') || 'the business, target audience, and brand style';
};

const hasImageThemeSuggestionContext = (value) =>
  Boolean(value?.business_summary?.trim() || value?.website_url?.trim() || asArray(value?.locations).length > 0);

const cleanThemeCandidate = (value) => {
  const words = value
    .replace(/https?:\/\/\S+/gi, ' ')
    .replace(/[^a-z0-9&/ -]/gi, ' ')
    .split(/\s+/)
    .map((word) => word.trim())
    .filter((word) => word && !themeStopWords.has(word.toLowerCase()));

  return words.slice(0, 6).join(' ');
};

const extractThemeLabelsFromContext = (value) => {
  const context = `${value?.business_summary || ''} ${value?.website_url || ''}`
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim();
  const chunks = context
    .split(/[,.;|()\n\r]+|\s+(?:and|or|for)\s+/i)
    .filter((item) => !/\b(?:business is|business name|company is|brand is)\b/i.test(item))
    .map(cleanThemeCandidate)
    .filter((item) => item.split(' ').length >= 2 && item.length <= 60);
  const labels = [];

  chunks.forEach((chunk) => {
    const label = titleCase(chunk);
    if (!labels.some((item) => item.toLowerCase() === label.toLowerCase())) {
      labels.push(label);
    }
  });

  if (labels.length >= 2) return labels.slice(0, 6);

  const fallbackWords = cleanThemeCandidate(context).split(' ').filter(Boolean);
  if (fallbackWords.length >= 2) {
    labels.push(titleCase(fallbackWords.slice(0, Math.min(4, fallbackWords.length)).join(' ')));
  }

  return labels.slice(0, 4);
};

const getSuggestedThemeLabels = (value) => {
  const labels = extractThemeLabelsFromContext(value);
  return labels.length > 0 ? labels : [];
};

const themePromptAngles = [
  'Use a polished editorial composition with a clear foreground subject, realistic environment, and premium lighting.',
  'Use a customer-outcome composition that shows the finished result, practical benefit, and aspirational brand mood.',
  'Use a close-detail composition that highlights materials, craftsmanship, texture, and quality signals.',
  'Use a wide lifestyle composition that shows the full setting, daily-use context, and local market relevance.',
  'Use a clean promotional composition with generous whitespace for social copy while keeping the visual realistic.',
  'Use a modern portfolio-style composition that feels custom-made for this exact business, not a generic stock image.',
];

const themeColorPalette = ['#1D4ED8', '#047857', '#B45309', '#7C3AED', '#BE123C', '#0F766E'];

const getContextColorIndex = (value = '') =>
  value.split('').reduce((total, char) => total + char.charCodeAt(0), 0) % themeColorPalette.length;

const getSuggestedThemeColor = (value, index) => {
  const existingColors = asArray(value?.image_themes)
    .map((item) => item?.theme_color)
    .filter(Boolean);
  if (existingColors.length > 0) return existingColors[index % existingColors.length];
  const context = `${value?.website_url || ''} ${value?.business_summary || ''}`.trim();
  if (!context) return '';
  return themeColorPalette[(getContextColorIndex(context) + index) % themeColorPalette.length];
};

const buildThemePrompt = ({ label, context, themeColor, index }) =>
  [
    `Create a distinct social media image for the "${label}" theme.`,
    `Theme focus: ${label}.`,
    `Business context: ${context}.`,
    themePromptAngles[index % themePromptAngles.length],
    themeColor
      ? `Use ${themeColor} as the main brand accent color in decor, props, background accents, lighting accents, or graphic details without overpowering the scene.`
      : '',
    'Make this image visually different from the other theme rows and avoid repeated layouts, repeated subjects, and generic stock-photo composition.',
  ]
    .filter(Boolean)
    .join(' ');

const buildImageThemeSuggestions = (value) => {
  const context = getBusinessContext(value);
  return getSuggestedThemeLabels(value).map((label, index) => {
    const themeColor = getSuggestedThemeColor(value, index);
    return {
      label,
      theme_color: themeColor,
      prompt: buildThemePrompt({ label, context, themeColor, index }),
    };
  });
};

const isAutoReplaceableImageThemes = (themes) => {
  const rows = asArray(themes);
  if (rows.length === 0) return true;

  const defaultLabels = new Set([
    ...legacyAutoImageThemeLabels,
    'Primary service visual',
    'Customer outcome visual',
    'Product/service scene',
    'Brand visual',
  ]);

  return rows.every((item) => {
    const label = item?.label?.trim();
    const prompt = item?.prompt?.trim();
    return (
      (!label && !prompt) ||
      defaultLabels.has(label) ||
      prompt.includes('Create a distinct social media image for the') ||
      prompt.includes('Use this business context:') ||
      prompt.includes('polished real-world setting') ||
      prompt.includes('finished result and customer benefit')
    );
  });
};

const emptyValidationErrors = {
  website: {
    website_url: '',
    business_summary: '',
    content_language: '',
  },
  prompts: {
    content_types: '',
    content_prompts_group: '',
    content_prompts: [],
    image_prompts_group: '',
    image_prompts: [],
    image_themes_group: '',
    image_themes: [],
    reference_images: [],
    logo_media_url: '',
    logo_position: '',
  },
  schedule: {
    general: '',
    posts_per_day_default: '',
    days: {},
  },
};

const getStepErrorFlags = (errors) => ({
  0: Boolean(errors.website.business_summary || errors.website.content_language),
  1: Boolean(
      errors.prompts.content_types ||
      errors.prompts.content_prompts_group ||
      errors.prompts.image_prompts_group ||
      errors.prompts.image_themes_group ||
      errors.prompts.logo_media_url ||
      errors.prompts.logo_position ||
      errors.prompts.content_prompts.some((item) => item.label || item.prompt) ||
      errors.prompts.image_prompts.some((item) => item.label || item.prompt) ||
      errors.prompts.image_themes.some((item) => item.label || item.prompt) ||
      errors.prompts.reference_images.some((item) => item.description)
  ),
  3: Boolean(
    errors.schedule.general ||
      Object.values(errors.schedule.days).some((item) => item.posts_per_day || item.times)
  ),
});

const validatePromptRows = (prompts, isRequired) => {
  const rows = asArray(prompts);
  const rowErrors = rows.map((item) => {
    const hasLabel = Boolean(item?.label?.trim());
    const hasPrompt = Boolean(item?.prompt?.trim());
    const shouldValidate = isRequired || hasLabel || hasPrompt;

    return {
      label: shouldValidate && !hasLabel ? 'Label is required' : '',
      prompt: shouldValidate && !hasPrompt ? 'Prompt is required' : '',
    };
  });

  return {
    group: isRequired && rows.length === 0 ? 'Add at least one prompt' : '',
    rows: rowErrors,
  };
};

const validateImageThemeRows = (themes, isRequired) => {
  const rows = asArray(themes);
  let hasCompleteTheme = false;
  const rowErrors = rows.map((item) => {
    const hasLabel = Boolean(item?.label?.trim());
    const hasPrompt = Boolean(item?.prompt?.trim());
    const shouldValidate = isRequired || hasLabel || hasPrompt;
    if (hasLabel && hasPrompt) hasCompleteTheme = true;

    return {
      label: shouldValidate && !hasLabel ? 'Theme label is required' : '',
      prompt: shouldValidate && !hasPrompt ? 'Image generation prompt is required' : '',
    };
  });

  return {
    group: isRequired && !hasCompleteTheme ? 'Add at least one image generation theme' : '',
    rows: rowErrors,
  };
};

const validateConfiguration = (value) => {
  const normalized = normalizeConfig(value);
  const hasImageContent = asArray(normalized.content_types).includes('Image');
  const contentPromptErrors = validatePromptRows(normalized.content_prompts, true);
  const imagePromptErrors = validatePromptRows(normalized.image_prompts, hasImageContent);
  const imageThemeErrors = validateImageThemeRows(normalized.image_themes, hasImageContent);

  const errors = {
    website: {
      website_url: '',
      business_summary: normalized.business_summary.trim() ? '' : 'Business summary is required',
      content_language: normalized.content_language.trim() ? '' : 'Content language is required',
    },
    prompts: {
      content_types: asArray(normalized.content_types).length > 0 ? '' : 'Select at least one content type',
      content_prompts_group: contentPromptErrors.group,
      content_prompts: contentPromptErrors.rows,
      image_prompts_group: imagePromptErrors.group,
      image_prompts: imagePromptErrors.rows,
      image_themes_group: imageThemeErrors.group,
      image_themes: imageThemeErrors.rows,
      reference_images: normalized.reference_images.map((item) => ({
        description: item.media_url && !item.description.trim() ? 'Describe what AI should learn from this reference image' : '',
      })),
      logo_media_url: normalized.logo_settings.use_logo && !normalized.logo_settings.media_url ? 'Upload a business logo' : '',
      logo_position:
        normalized.logo_settings.use_logo && normalized.logo_settings.media_url && !normalized.logo_settings.position
          ? 'Select logo position'
          : '',
    },
    schedule: {
      general:
        normalized.let_ai_choose_schedule || asArray(normalized.schedule).length > 0 ? '' : 'Select at least one posting day',
      posts_per_day_default: '',
      days: {},
    },
  };

  if (!normalized.let_ai_choose_schedule) {
    asArray(normalized.schedule).forEach((item) => {
      const postsPerDay = Number(item.posts_per_day);
      const times = asArray(item.times);
      errors.schedule.days[item.day_of_week] = {
        posts_per_day: postsPerDay > 0 ? '' : `Posts on ${formatDay(item.day_of_week)} must be at least 1`,
        times:
          postsPerDay > 0 && times.length < postsPerDay
            ? `Add ${postsPerDay} time${postsPerDay === 1 ? '' : 's'} for ${formatDay(item.day_of_week)}`
            : '',
      };
    });
  }

  const stepErrors = getStepErrorFlags(errors);
  const firstErrorStep = steps.findIndex((_, index) => stepErrors[index]);

  return {
    errors,
    stepErrors,
    hasErrors: firstErrorStep !== -1,
    firstErrorStep,
  };
};

const AIAutoPilotV2 = ({ imageModels = [], selectedImageModel }) => {
  const { showToast } = useToaster();
  const [activeStep, setActiveStep] = useState(0);
  const [config, setConfig] = useState(() => normalizeConfig());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [chipInputs, setChipInputs] = useState({ locations: '', negative_words: '', hashtags: '' });
  const [timeInputs, setTimeInputs] = useState({});
  const [fileUploadProgress, setFileUploadprogress] = useState(0);
  const [uploadingKey, setUploadingKey] = useState('');
  const [validationAttempted, setValidationAttempted] = useState(false);
  const [validationErrors, setValidationErrors] = useState(emptyValidationErrors);
  const [stepErrorFlags, setStepErrorFlags] = useState({});
  const [runErrorMessage, setRunErrorMessage] = useState('');
  const [isEditingConfiguration, setIsEditingConfiguration] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const isAutoPilotRunning = config.auto_pilot_status === 'running';
  const isAutoPilotPaused = config.auto_pilot_status === 'paused';
  const isGenerationRunning = config.generation_status === 'generating';
  const isGenerationFailed = config.generation_status === 'failed' || config.auto_pilot_status === 'failed';
  const hasAutoPilotStatus = isAutoPilotRunning || isAutoPilotPaused || isGenerationFailed;
  const hasActiveAutoPilot = isAutoPilotRunning || isAutoPilotPaused;
  const showConfigurationStepper = !hasAutoPilotStatus || isEditingConfiguration;

  const selectedModel = useMemo(() => selectedImageModel || imageModels?.[0] || null, [imageModels, selectedImageModel]);
  const previewPlan = useMemo(() => {
    const schedule = asArray(config.schedule);
    const manualSchedule = config.let_ai_choose_schedule ? [] : schedule;
    return {
      totalPosts: getTotalPosts(config),
      selectedDays: manualSchedule.length,
      scheduledTimes: manualSchedule.reduce((total, item) => total + asArray(item.times).length, 0),
      contentPromptCount: getCompletedPromptCount(config.content_prompts),
      imagePromptCount: getCompletedPromptCount(config.image_prompts),
      imageThemeCount: getCompletedPromptCount(config.image_themes),
      referenceImageCount: asArray(config.reference_images).filter((item) => item.media_url).length,
      contentTypes: asArray(config.content_types),
    };
  }, [config]);

  useEffect(() => {
    fetchConfiguration();
  }, []);

  useEffect(() => {
    if (!isGenerationRunning) return undefined;
    const interval = setInterval(() => fetchConfiguration({ silent: true }), 10000);
    return () => clearInterval(interval);
  }, [isGenerationRunning]);

  useEffect(() => {
    if (selectedModel) {
      setConfig((prev) => ({
        ...prev,
        model: prev.model || selectedModel.model,
        model_provider: prev.model_provider || selectedModel.model_provider,
      }));
    }
  }, [selectedModel]);

  useEffect(() => {
    if (validationAttempted) {
      const result = validateConfiguration(config);
      setValidationErrors(result.errors);
      setStepErrorFlags(result.stepErrors);
    }
  }, [config, validationAttempted]);

  const fetchConfiguration = ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    HttpServices.Get({
      url: postsModuleURL.getAIAutoPilotV2Configuration,
      successCallback: (data) => {
        const normalizedData = normalizeConfig(data);
        if (!hasImageThemeSuggestionContext(normalizedData) && isAutoReplaceableImageThemes(normalizedData.image_themes)) {
          normalizedData.image_themes = [];
        }
        setConfig(normalizedData);
        if (!silent) setRunErrorMessage('');
        if (!silent) setLoading(false);
      },
      failureCallback: (message) => {
        if (!silent) showToast(message || 'Unable to load AI Auto Pilot configuration', 'e');
        if (!silent) setLoading(false);
      },
    });
  };

  const updateConfig = (key, value) => setConfig((prev) => ({ ...prev, [key]: value }));

  const addChip = (key) => {
    const value = chipInputs[key].trim();
    if (!value) return;
    const currentValues = asArray(config[key]);
    const nextValues = value
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item && !currentValues.includes(item));
    updateConfig(key, [...currentValues, ...nextValues]);
    setChipInputs((prev) => ({ ...prev, [key]: '' }));
  };

  const deleteChip = (key, value) => updateConfig(key, asArray(config[key]).filter((item) => item !== value));

  const updateContentTypes = (value) => {
    setConfig((prev) => {
      const selectedTypes = asArray(value);
      const hadImage = asArray(prev.content_types).includes('Image');
      const nextConfig = { ...prev, content_types: selectedTypes };
      if (
        selectedTypes.includes('Image') &&
        !hadImage &&
        hasImageThemeSuggestionContext(nextConfig) &&
        isAutoReplaceableImageThemes(prev.image_themes)
      ) {
        nextConfig.image_themes = buildImageThemeSuggestions(nextConfig);
      }
      return nextConfig;
    });
  };

  const updatePrompt = (key, index, field, value) => {
    const nextPrompts = [...asArray(config[key])];
    nextPrompts[index] = { ...nextPrompts[index], [field]: value };
    updateConfig(key, nextPrompts);
  };

  const addPrompt = (key) => updateConfig(key, [...asArray(config[key]), { ...emptyPrompt }]);
  const deletePrompt = (key, index) =>
    updateConfig(
      key,
      asArray(config[key]).filter((_, promptIndex) => promptIndex !== index)
    );

  const updateImageTheme = (index, field, value) => {
    const nextThemes = [...asArray(config.image_themes)];
    nextThemes[index] = { ...nextThemes[index], [field]: value };
    updateConfig('image_themes', nextThemes);
  };

  const addImageTheme = () => updateConfig('image_themes', [...asArray(config.image_themes), { ...emptyImageTheme }]);
  const suggestImageThemes = () => {
    if (!hasImageThemeSuggestionContext(config)) {
      showToast('Add business summary or website before suggesting image themes', 'e');
      return;
    }
    const suggestedThemes = buildImageThemeSuggestions(config);
    if (suggestedThemes.length === 0) {
      showToast('Add more business details so AI can identify theme titles', 'e');
      return;
    }
    updateConfig('image_themes', suggestedThemes);
  };
  const deleteImageTheme = (index) =>
    updateConfig(
      'image_themes',
      asArray(config.image_themes).filter((_, themeIndex) => themeIndex !== index)
    );

  const updateReferenceImage = (index, field, value) => {
    const nextImages = [...asArray(config.reference_images)];
    nextImages[index] = { ...nextImages[index], [field]: value };
    updateConfig('reference_images', nextImages);
  };

  const addReferenceImage = () => updateConfig('reference_images', [...asArray(config.reference_images), { ...emptyReferenceImage }]);
  const deleteReferenceImage = (index) =>
    updateConfig(
      'reference_images',
      asArray(config.reference_images).filter((_, imageIndex) => imageIndex !== index)
    );

  const updateLogoSettings = (value) => updateConfig('logo_settings', { ...normalizeLogoSettings(config.logo_settings), ...value });

  const uploadMediaFile = (files, key, successCallback) => {
    const selectedFiles = Array.from(files || []);
    if (selectedFiles.length === 0) return;
    const invalidFile = selectedFiles.find((file) => {
      const extension = file.name?.split('.').pop()?.toLowerCase();
      return !allowedUploadExtensions.includes(extension) || !allowedUploadMimeTypes.includes(file.type);
    });
    if (invalidFile) {
      showToast('Upload only JPG, JPEG, PNG, or WEBP images', 'e');
      return;
    }
    setUploadingKey(key);
    CommonHelper.UploadMediaFilesToServer({
      files: selectedFiles,
      setFileUploadprogress,
      successCallback: (data, message) => {
        setUploadingKey('');
        showToast(message || 'File uploaded', 's');
        successCallback(asArray(data));
      },
      failureCallback: (message) => {
        setUploadingKey('');
        showToast(message || 'Unable to upload file', 'e');
      },
    });
  };

  const uploadReferenceImage = (index, files) => {
    uploadMediaFile(files, `reference-${index}`, (uploadedFiles) => {
      const uploadedFile = uploadedFiles[0];
      if (!uploadedFile) return;
      const nextImages = [...asArray(config.reference_images)];
      nextImages[index] = {
        ...nextImages[index],
        media_url: uploadedFile.media_url || '',
        thumbnail_urls: uploadedFile.thumbnail_urls || [],
      };
      updateConfig('reference_images', nextImages);
    });
  };

  const uploadLogo = (files) => {
    uploadMediaFile(files, 'logo', (uploadedFiles) => {
      const uploadedFile = uploadedFiles[0];
      if (!uploadedFile) return;
      updateLogoSettings({
        use_logo: true,
        media_url: uploadedFile.media_url || '',
        thumbnail_urls: uploadedFile.thumbnail_urls || [],
      });
    });
  };

  const analyzeWebsite = () => {
    if (!config.website_url.trim()) {
      showToast('Enter business website first', 'e');
      return;
    }
    setSaving(true);
    HttpServices.Post({
      url: postsModuleURL.analyzeAIAutoPilotV2Website,
      body: { website_url: config.website_url.trim() },
      successCallback: (data, message) => {
        setConfig((prev) => {
          const mergedConfig = normalizeConfig({ ...prev, ...(data || {}), website_url: prev.website_url.trim() });
          if (
            asArray(mergedConfig.content_types).includes('Image') &&
            hasImageThemeSuggestionContext(mergedConfig) &&
            isAutoReplaceableImageThemes(prev.image_themes) &&
            asArray(mergedConfig.image_themes).length === 0
          ) {
            mergedConfig.image_themes = buildImageThemeSuggestions(mergedConfig);
          }
          return mergedConfig;
        });
        showToast(message || 'Website analyzed', 's');
        setSaving(false);
      },
      failureCallback: (message) => {
        showToast(message || 'Unable to analyze website', 'e');
        setSaving(false);
      },
    });
  };

  const saveConfiguration = (afterSave) => {
    const normalizedConfig = normalizeConfig(config);
    const hasImageContent = asArray(normalizedConfig.content_types).includes('Image');
    const payload = {
      ...normalizedConfig,
      model: selectedModel?.model || config.model,
      model_provider: selectedModel?.model_provider || config.model_provider,
      content_prompts: normalizedConfig.content_prompts.filter((item) => item.prompt.trim()),
      image_prompts: hasImageContent ? normalizedConfig.image_prompts.filter((item) => item.prompt.trim()) : [],
      image_themes: hasImageContent ? normalizedConfig.image_themes.filter((item) => item.prompt.trim()) : [],
      reference_images: hasImageContent ? normalizedConfig.reference_images : [],
      logo_settings: hasImageContent ? normalizedConfig.logo_settings : normalizeLogoSettings(),
      schedule: normalizedConfig.let_ai_choose_schedule
        ? []
        : normalizedConfig.schedule.filter((item) => item.posts_per_day > 0 && item.times.length > 0),
    };
    setSaving(true);
    HttpServices.Post({
      url: postsModuleURL.saveAIAutoPilotV2Configuration,
      body: { configuration: payload },
      successCallback: (data, message) => {
        showToast(message || 'Configuration saved', 's');
        const nextConfig = normalizeConfig(payload);
        setConfig(nextConfig);
        if (nextConfig.auto_pilot_status === 'running' || nextConfig.auto_pilot_status === 'paused') {
          setIsEditingConfiguration(false);
        }
        setSaving(false);
        if (typeof afterSave === 'function') afterSave();
      },
      failureCallback: (message) => {
        showToast(message || 'Unable to save configuration', 'e');
        setSaving(false);
      },
    });
  };

  const generateAndSchedule = () => {
    setRunErrorMessage('');
    const validationResult = validateConfiguration(config);
    setValidationAttempted(true);
    setValidationErrors(validationResult.errors);
    setStepErrorFlags(validationResult.stepErrors);

    if (validationResult.hasErrors) {
      setActiveStep(validationResult.firstErrorStep);
      showToast('Please fix the highlighted AI Auto Pilot fields', 'e');
      return;
    }

    saveConfiguration(() => {
      setSaving(true);
      HttpServices.Post({
        url: postsModuleURL.generateAIAutoPilotV2,
        body: {},
        successCallback: (data, message) => {
          showToast(message || 'AI is generating content and images', 's');
          setRunErrorMessage('');
          setIsEditingConfiguration(false);
          if (data) {
            setConfig(normalizeConfig(data));
          } else {
            fetchConfiguration();
          }
          setSaving(false);
        },
        failureCallback: (message) => {
          const errorMessage = message || 'Unable to queue generation';
          setRunErrorMessage(errorMessage);
          showToast(errorMessage, 'e');
          fetchConfiguration({ silent: true });
          setSaving(false);
        },
      });
    });
  };

  const pauseAutoPilot = () => {
    setSaving(true);
    HttpServices.Post({
      url: postsModuleURL.pauseAIAutoPilotV2,
      body: {},
      successCallback: (data, message) => {
        showToast(message || 'AI Auto Pilot paused', 's');
        setRunErrorMessage('');
        setConfig(normalizeConfig(data));
        setIsEditingConfiguration(false);
        setSaving(false);
      },
      failureCallback: (message) => {
        showToast(message || 'Unable to pause AI Auto Pilot', 'e');
        setSaving(false);
      },
    });
  };

  const resumeAutoPilot = () => {
    setSaving(true);
    HttpServices.Post({
      url: postsModuleURL.resumeAIAutoPilotV2,
      body: {},
      successCallback: (data, message) => {
        showToast(message || 'AI Auto Pilot resumed', 's');
        setRunErrorMessage('');
        setConfig(normalizeConfig(data));
        setIsEditingConfiguration(false);
        setSaving(false);
      },
      failureCallback: (message) => {
        showToast(message || 'Unable to resume AI Auto Pilot', 'e');
        setSaving(false);
      },
    });
  };

  const deleteAutoPilot = () => {
    setSaving(true);
    HttpServices.Post({
      url: postsModuleURL.deleteAIAutoPilotV2,
      body: {},
      successCallback: (data, message) => {
        showToast(message || 'AI Auto Pilot deleted', 's');
        setConfig(normalizeConfig());
        setActiveStep(0);
        setValidationAttempted(false);
        setValidationErrors(emptyValidationErrors);
        setStepErrorFlags({});
        setRunErrorMessage('');
        setIsEditingConfiguration(false);
        setIsDeleteDialogOpen(false);
        setSaving(false);
      },
      failureCallback: (message) => {
        showToast(message || 'Unable to delete AI Auto Pilot', 'e');
        setSaving(false);
      },
    });
  };

  const toggleScheduleDay = (day) => {
    const schedule = asArray(config.schedule);
    const exists = schedule.some((item) => item.day_of_week === day);
    if (exists) {
      updateConfig('schedule', schedule.filter((item) => item.day_of_week !== day));
      return;
    }
    updateConfig('schedule', [...schedule, { day_of_week: day, posts_per_day: 1, times: ['10:00'] }]);
  };

  const updateSchedule = (day, key, value) => {
    updateConfig(
      'schedule',
      asArray(config.schedule).map((item) => (item.day_of_week === day ? { ...item, [key]: value } : item))
    );
  };

  const addScheduleTime = (day) => {
    const value = timeInputs[day];
    if (!value) return;
    const nextSchedule = asArray(config.schedule).map((item) =>
      item.day_of_week === day && !asArray(item.times).includes(value)
        ? { ...item, times: [...asArray(item.times), value] }
        : item
    );
    updateConfig('schedule', nextSchedule);
    setTimeInputs((prev) => ({ ...prev, [day]: '' }));
  };

  const deleteScheduleTime = (day, value) => {
    updateConfig(
      'schedule',
      asArray(config.schedule).map((item) =>
        item.day_of_week === day ? { ...item, times: asArray(item.times).filter((time) => time !== value) } : item
      )
    );
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: 360, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spinner />
      </Box>
    );
  }

  const selectedDays = new Set(asArray(config.schedule).map((item) => item.day_of_week));
  const hasImageContent = asArray(config.content_types).includes('Image');

  return (
    <Box sx={{ maxWidth: 1180, mx: 'auto', px: { xs: 1, md: 3 }, pt: 2, pb: { xs: 10, md: 12 } }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} gap={2}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            AI Auto Pilot Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Fresh setup with website prefill, custom prompts, content rules, and editable scheduling.
          </Typography>
        </Box>
      </Stack>

      {hasAutoPilotStatus && showConfigurationStepper && (
        <Box
          sx={{
            mt: 2,
            px: 2,
            py: 1.5,
            border: '1px solid',
            borderColor: isGenerationFailed ? 'error.light' : isAutoPilotRunning ? 'success.light' : 'warning.light',
            borderRadius: 1.5,
            bgcolor: isGenerationFailed ? 'error.lighter' : isAutoPilotRunning ? 'success.lighter' : 'warning.lighter',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          <Stack direction="row" alignItems="center" gap={1.25}>
            <AutoAwesomeIcon color={isGenerationFailed ? 'error' : isAutoPilotRunning ? 'success' : 'warning'} />
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              {isGenerationFailed
                ? 'AI Auto Post is not running'
                : isAutoPilotRunning
                ? 'AI Auto Post is currently running'
                : 'AI Auto Post is paused'}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Last run: {isGenerationRunning ? 'Running now' : formatDateTime(config.last_run_at)}. Next run:{' '}
            {formatDateTime(config.next_run_at)}.
          </Typography>
          {isGenerationFailed && config.last_run_message && (
            <Typography variant="body2" color="error.main" sx={{ width: '100%', fontWeight: 700 }}>
              {config.last_run_message}
            </Typography>
          )}
        </Box>
      )}

      {hasAutoPilotStatus && !showConfigurationStepper && (
        <Box
          sx={{
            minHeight: { xs: 'calc(100vh - 240px)', md: 'calc(100vh - 260px)' },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 5,
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: 720,
              border: '1px solid',
              borderColor: isGenerationFailed ? 'error.light' : isAutoPilotRunning ? 'success.light' : 'warning.light',
              borderRadius: 2,
              bgcolor: '#fff',
              boxShadow: '0 14px 40px rgba(15, 23, 42, 0.08)',
              p: { xs: 2.5, md: 4 },
              textAlign: 'center',
            }}
          >
            <Stack alignItems="center" gap={2}>
              <Box
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: isGenerationFailed ? 'error.lighter' : isAutoPilotRunning ? 'success.lighter' : 'warning.lighter',
                  color: isGenerationFailed ? 'error.dark' : isAutoPilotRunning ? 'success.dark' : 'warning.dark',
                }}
              >
                <AutoAwesomeIcon fontSize="large" />
              </Box>
              <Chip
                color={isGenerationFailed ? 'error' : isAutoPilotRunning ? 'success' : 'warning'}
                label={isGenerationFailed ? 'Not running' : isAutoPilotRunning ? 'Running' : 'Paused'}
                sx={{ fontWeight: 700 }}
              />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
                  {isGenerationFailed
                    ? 'AI Auto Post is not running'
                    : isAutoPilotRunning
                    ? 'AI Auto Post is currently running'
                    : 'AI Auto Post is paused'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Runs every {config.scheduler_interval_hours || 12} hours. Last run:{' '}
                  {isGenerationRunning ? 'Running now' : formatDateTime(config.last_run_at)}.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Next run: {formatDateTime(config.next_run_at)}.
                </Typography>
                <Typography
                  variant="body2"
                  color={isGenerationRunning ? 'warning.main' : isGenerationFailed ? 'error.main' : 'success.main'}
                  sx={{ mt: 1, fontWeight: 700 }}
                >
                  {isGenerationRunning
                    ? 'AI is generating images and content. This will update when scheduling is completed.'
                    : config.last_run_message || 'Check the generated and scheduled posts in the Upcoming section.'}
                </Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="center" gap={1.25} sx={{ width: '100%', pt: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<EditOutlinedIcon />}
                  onClick={() => {
                    setIsEditingConfiguration(true);
                    setActiveStep(0);
                  }}
                  disabled={saving}
                >
                  Edit
                </Button>
                {!isGenerationFailed && (
                  <Button
                    variant="outlined"
                    color={isAutoPilotRunning ? 'warning' : 'success'}
                    startIcon={isAutoPilotRunning ? <PauseCircleOutlineIcon /> : <PlayCircleOutlineIcon />}
                    onClick={isAutoPilotRunning ? pauseAutoPilot : resumeAutoPilot}
                    disabled={saving}
                  >
                    {isAutoPilotRunning ? 'Pause' : 'Resume'}
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => setIsDeleteDialogOpen(true)}
                  disabled={saving}
                >
                  Delete
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Box>
      )}

      {showConfigurationStepper && (
        <>
          {runErrorMessage && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {runErrorMessage}
            </Alert>
          )}
          <Stepper activeStep={activeStep} alternativeLabel sx={{ my: 3 }}>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel error={Boolean(stepErrorFlags[index])}>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: { xs: 2, md: 3 }, bgcolor: '#fff' }}>
        {activeStep === 0 && (
          <Grid container spacing={2.5}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Business website"
                value={config.website_url}
                onChange={(e) => updateConfig('website_url', e.target.value)}
                InputProps={{ startAdornment: <PublicIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button fullWidth variant="contained" sx={{ height: '100%' }} onClick={analyzeWebsite} disabled={saving}>
                Analyze Website
              </Button>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                label="Business summary"
                value={config.business_summary}
                onChange={(e) => updateConfig('business_summary', e.target.value)}
                error={Boolean(validationErrors.website.business_summary)}
                helperText={validationErrors.website.business_summary}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Content language"
                value={config.content_language}
                onChange={(e) => updateConfig('content_language', e.target.value)}
                error={Boolean(validationErrors.website.content_language)}
                helperText={validationErrors.website.content_language}
                InputProps={{ startAdornment: <LanguageIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Locations"
                value={chipInputs.locations}
                onChange={(e) => setChipInputs((prev) => ({ ...prev, locations: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addChip('locations'))}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={() => addChip('locations')}>
                      <AddIcon />
                    </IconButton>
                  ),
                }}
              />
              <ChipList values={config.locations} onDelete={(value) => deleteChip('locations', value)} />
            </Grid>
          </Grid>
        )}

        {activeStep === 1 && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth error={Boolean(validationErrors.prompts.content_types)}>
                <InputLabel>Content types</InputLabel>
                <Select
                  multiple
                  label="Content types"
                  value={asArray(config.content_types)}
                  onChange={(e) => updateContentTypes(e.target.value)}
                  renderValue={(selected) => asArray(selected).join(', ')}
                >
                  {contentTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      <Checkbox checked={asArray(config.content_types).includes(type)} />
                      {type}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{validationErrors.prompts.content_types}</FormHelperText>
              </FormControl>
            </Grid>
            <PromptEditor
              title="Content prompts"
              values={config.content_prompts}
              errors={validationErrors.prompts.content_prompts}
              groupError={validationErrors.prompts.content_prompts_group}
              onChange={(index, field, value) => updatePrompt('content_prompts', index, field, value)}
              onAdd={() => addPrompt('content_prompts')}
              onDelete={(index) => deletePrompt('content_prompts', index)}
            />
            {hasImageContent && (
              <>
                <PromptEditor
                  title="Image prompts"
                  values={config.image_prompts}
                  errors={validationErrors.prompts.image_prompts}
                  groupError={validationErrors.prompts.image_prompts_group}
                  onChange={(index, field, value) => updatePrompt('image_prompts', index, field, value)}
                  onAdd={() => addPrompt('image_prompts')}
                  onDelete={(index) => deletePrompt('image_prompts', index)}
                />
                <ImageThemeEditor
                  values={config.image_themes}
                  errors={validationErrors.prompts.image_themes}
                  groupError={validationErrors.prompts.image_themes_group}
                  onChange={updateImageTheme}
                  onAdd={addImageTheme}
                  onSuggest={suggestImageThemes}
                  onDelete={deleteImageTheme}
                />
                <ReferenceImageEditor
                  values={config.reference_images}
                  errors={validationErrors.prompts.reference_images}
                  uploadingKey={uploadingKey}
                  uploadProgress={fileUploadProgress}
                  onAdd={addReferenceImage}
                  onDelete={deleteReferenceImage}
                  onChange={updateReferenceImage}
                  onUpload={uploadReferenceImage}
                />
                <LogoSettingsEditor
                  value={config.logo_settings}
                  errors={{
                    media_url: validationErrors.prompts.logo_media_url,
                    position: validationErrors.prompts.logo_position,
                  }}
                  uploadingKey={uploadingKey}
                  uploadProgress={fileUploadProgress}
                  onChange={updateLogoSettings}
                  onUpload={uploadLogo}
                />
              </>
            )}
          </Grid>
        )}

        {activeStep === 2 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Negative words"
                value={chipInputs.negative_words}
                onChange={(e) => setChipInputs((prev) => ({ ...prev, negative_words: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addChip('negative_words'))}
                InputProps={{
                  startAdornment: <ShieldIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: (
                    <IconButton onClick={() => addChip('negative_words')}>
                      <AddIcon />
                    </IconButton>
                  ),
                }}
              />
              <ChipList values={config.negative_words} onDelete={(value) => deleteChip('negative_words', value)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Hashtags"
                value={chipInputs.hashtags}
                onChange={(e) => setChipInputs((prev) => ({ ...prev, hashtags: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addChip('hashtags'))}
                InputProps={{
                  startAdornment: <TagIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: (
                    <IconButton onClick={() => addChip('hashtags')}>
                      <AddIcon />
                    </IconButton>
                  ),
                }}
              />
              <ChipList values={config.hashtags} onDelete={(value) => deleteChip('hashtags', value)} />
              <FormControlLabel
                control={
                  <Switch
                    checked={config.include_hashtags}
                    onChange={(e) => updateConfig('include_hashtags', e.target.checked)}
                  />
                }
                label="Generate hashtags in content"
              />
            </Grid>
            <Grid item xs={12}>
              <SafetyPreview
                language={config.content_language}
                locations={config.locations}
                negativeWords={config.negative_words}
                hashtags={config.hashtags}
                includeHashtags={config.include_hashtags}
              />
            </Grid>
          </Grid>
        )}

        {activeStep === 3 && (
          <Stack gap={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={config.let_ai_choose_schedule}
                  onChange={(e) => updateConfig('let_ai_choose_schedule', e.target.checked)}
                />
              }
              label="Let AI choose days and timing"
            />
            {config.let_ai_choose_schedule ? (
              <Alert severity="info">
                AI will choose the posting days and times based on the business summary, content prompts, language,
                location, and safety rules. You can turn this off to set specific weekdays and times manually.
              </Alert>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary">
                  {asArray(config.schedule).length} days selected
                </Typography>
                {validationErrors.schedule.general && (
                  <FormHelperText error sx={{ mt: -1 }}>
                    {validationErrors.schedule.general}
                  </FormHelperText>
                )}
                <Grid container spacing={2}>
                  {days.map((day) => {
                    const dayConfig = asArray(config.schedule).find((item) => item.day_of_week === day);
                    const dayErrors = validationErrors.schedule.days[day] || {};
                    return (
                      <Grid item xs={12} md={6} key={day}>
                        <Box
                          sx={{
                            border: '1px solid',
                            borderColor: dayErrors.posts_per_day || dayErrors.times ? 'error.main' : 'divider',
                            borderRadius: 1.5,
                            p: 2,
                          }}
                        >
                          <FormControlLabel
                            control={<Checkbox checked={selectedDays.has(day)} onChange={() => toggleScheduleDay(day)} />}
                            label={formatDay(day)}
                          />
                          {dayConfig && (
                            <Stack gap={1.5}>
                              <TextField
                                type="number"
                                label={`Posts on ${formatDay(day)}`}
                                value={dayConfig.posts_per_day}
                                onChange={(e) => updateSchedule(day, 'posts_per_day', Number(e.target.value))}
                                error={Boolean(dayErrors.posts_per_day)}
                                helperText={dayErrors.posts_per_day}
                                inputProps={{ min: 1, max: 10 }}
                              />
                              <Stack direction="row" gap={1}>
                                <TextField
                                  type="time"
                                  label="Time"
                                  value={timeInputs[day] || ''}
                                  onChange={(e) => setTimeInputs((prev) => ({ ...prev, [day]: e.target.value }))}
                                  InputLabelProps={{ shrink: true }}
                                />
                                <Button variant="outlined" onClick={() => addScheduleTime(day)}>
                                  Add
                                </Button>
                              </Stack>
                              <ChipList values={dayConfig.times} onDelete={(value) => deleteScheduleTime(day, value)} />
                              {dayErrors.times && <FormHelperText error>{dayErrors.times}</FormHelperText>}
                            </Stack>
                          )}
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </>
            )}
          </Stack>
        )}

        {activeStep === 4 && (
          <Stack gap={3}>
            {validationAttempted && Object.values(stepErrorFlags).some(Boolean) && (
              <Alert severity="error">Some sections need attention before Generate & Schedule can run.</Alert>
            )}
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Generation Plan
              </Typography>
              <Grid container spacing={1.5}>
                <PreviewMetric label="Total posts" value={previewPlan.totalPosts || '-'} />
                <PreviewMetric label="Posting days" value={config.let_ai_choose_schedule ? 'AI chosen' : previewPlan.selectedDays || '-'} />
                <PreviewMetric label="Posting times" value={config.let_ai_choose_schedule ? 'AI chosen' : previewPlan.scheduledTimes || '-'} />
                <PreviewMetric label="Content prompts" value={previewPlan.contentPromptCount || '-'} />
                {hasImageContent && (
                  <>
                    <PreviewMetric label="Image prompts" value={previewPlan.imagePromptCount || '-'} />
                    <PreviewMetric label="Image themes" value={previewPlan.imageThemeCount || '-'} />
                    <PreviewMetric label="References" value={previewPlan.referenceImageCount || '-'} />
                    <PreviewMetric
                      label="Theme colors"
                      value={listValue(asArray(config.image_themes).map((item) => item.theme_color).filter(Boolean), '-')}
                    />
                  </>
                )}
              </Grid>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <PreviewPanel title="Business Context">
                  <ReviewRow label="Website" value={config.website_url || '-'} />
                  <ReviewRow label="Language" value={config.content_language || '-'} />
                  <ReviewRow label="Locations" value={listValue(config.locations, '-')} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {config.business_summary || '-'}
                  </Typography>
                </PreviewPanel>
              </Grid>
              <Grid item xs={12} md={6}>
                <PreviewPanel title="Schedule">
                  <ReviewRow label="Timing mode" value={config.let_ai_choose_schedule ? 'AI assisted' : 'Manual'} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {config.let_ai_choose_schedule
                      ? 'AI will choose the best days and times from the business and content context.'
                      : formatScheduleSummary(config.schedule)}
                  </Typography>
                </PreviewPanel>
              </Grid>
              <Grid item xs={12}>
                <SafetyPreview
                  language={config.content_language}
                  locations={config.locations}
                  negativeWords={config.negative_words}
                  hashtags={config.hashtags}
                  includeHashtags={config.include_hashtags}
                />
              </Grid>
              {hasImageContent && (
                <Grid item xs={12}>
                  <PreviewPanel title="Visual Generation">
                    <ReviewRow label="Content types" value={previewPlan.contentTypes.join(', ') || '-'} />
                    <ReviewRow label="Image theme rotation" value={listValue(asArray(config.image_themes).map((item) => item.label).filter(Boolean), '-')} />
                    <ReviewRow label="Theme colors" value={listValue(asArray(config.image_themes).map((item) => item.theme_color).filter(Boolean), '-')} />
                    <ReviewRow label="Reference images" value={`${previewPlan.referenceImageCount}`} />
                    <ReviewRow
                      label="Logo"
                      value={
                        config.logo_settings?.use_logo
                          ? `${config.logo_settings?.position || 'Position not selected'}`
                          : 'Not included'
                      }
                    />
                  </PreviewPanel>
                </Grid>
              )}
            </Grid>
          </Stack>
        )}
          </Box>

          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{
              position: 'sticky',
              bottom: 0,
              zIndex: 2,
              mt: 2,
              py: 2,
              bgcolor: 'background.default',
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Button disabled={activeStep === 0 || saving} onClick={() => setActiveStep((prev) => prev - 1)}>
              Back
            </Button>
            {activeStep === steps.length - 1 ? (
              <Stack direction="row" gap={1}>
                <Button variant="contained" startIcon={<AutoAwesomeIcon />} onClick={generateAndSchedule} disabled={saving}>
                  {hasActiveAutoPilot ? 'Update & Schedule' : 'Generate & Schedule'}
                </Button>
              </Stack>
            ) : (
              <Button variant="contained" disabled={saving} onClick={() => setActiveStep((prev) => prev + 1)}>
                {activeStep === steps.length - 2 ? 'Preview Plan' : 'Next'}
              </Button>
            )}
          </Stack>
        </>
      )}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => !saving && setIsDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Delete AI Auto Pilot configuration?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will delete the saved AI Auto Pilot setup and stop the current auto post schedule.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={() => setIsDeleteDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" color="error" startIcon={<DeleteOutlineIcon />} onClick={deleteAutoPilot} disabled={saving}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const PreviewMetric = ({ label, value }) => (
  <Grid item xs={6} md={2}>
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, minHeight: 82 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5, overflowWrap: 'anywhere' }}>
        {value}
      </Typography>
    </Box>
  </Grid>
);

const PreviewPanel = ({ title, children }) => (
  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, height: '100%' }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const SafetyPreview = ({ language, locations, negativeWords, hashtags, includeHashtags }) => (
  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={1.5} sx={{ mb: 1.5 }}>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Content Safety Preview
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Rules that will be sent with the AI generation request.
        </Typography>
      </Box>
      <Chip size="small" color={includeHashtags ? 'success' : 'default'} label={includeHashtags ? 'Hashtags enabled' : 'Hashtags disabled'} />
    </Stack>
    <Grid container spacing={1.5}>
      <Grid item xs={12} md={3}>
        <SafetyItem label="Language" value={language || '-'} />
      </Grid>
      <Grid item xs={12} md={3}>
        <SafetyItem label="Location focus" value={listValue(locations)} />
      </Grid>
      <Grid item xs={12} md={3}>
        <SafetyItem label="Avoid words" value={listValue(negativeWords)} />
      </Grid>
      <Grid item xs={12} md={3}>
        <SafetyItem label="Hashtags" value={includeHashtags ? listValue(hashtags, 'AI generated') : 'Not included'} />
      </Grid>
    </Grid>
  </Box>
);

const SafetyItem = ({ label, value }) => (
  <Box sx={{ bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.25, minHeight: 78 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5, overflowWrap: 'anywhere' }}>
      {value}
    </Typography>
  </Box>
);

const ChipList = ({ values, onDelete }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
    {asArray(values).map((value) => (
      <Chip key={value} label={value} onDelete={() => onDelete(value)} deleteIcon={<CloseIcon />} />
    ))}
  </Box>
);

const PromptEditor = ({ title, values, errors = [], groupError = '', onChange, onAdd, onDelete }) => (
  <Grid item xs={12}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      <Button variant="outlined" startIcon={<AddIcon />} onClick={onAdd}>
        Add
      </Button>
    </Stack>
    {groupError && (
      <FormHelperText error sx={{ mb: 1 }}>
        {groupError}
      </FormHelperText>
    )}
    <Stack gap={1.5}>
      {asArray(values).map((item, index) => {
        const rowErrors = asArray(errors)[index] || {};
        return (
        <Grid container spacing={1.5} key={`${title}-${index}`}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Label"
              value={item?.label || ''}
              error={Boolean(rowErrors.label)}
              helperText={rowErrors.label}
              onChange={(e) => onChange(index, 'label', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Prompt"
              value={item?.prompt || ''}
              error={Boolean(rowErrors.prompt)}
              helperText={rowErrors.prompt}
              onChange={(e) => onChange(index, 'prompt', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={() => onDelete(index)}>
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
        );
      })}
    </Stack>
  </Grid>
);

const ImageThemeEditor = ({ values, errors = [], groupError = '', onChange, onAdd, onSuggest, onDelete }) => {
  const [helpAnchorEl, setHelpAnchorEl] = useState(null);
  const isHelpOpen = Boolean(helpAnchorEl);

  return (
    <Grid item xs={12}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={1} sx={{ mb: 1 }}>
        <Box>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Image generation themes
            </Typography>
            <IconButton
              size="small"
              onMouseEnter={(event) => setHelpAnchorEl(event.currentTarget)}
              onMouseLeave={() => setHelpAnchorEl(null)}
            >
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
            <Popover
              open={isHelpOpen}
              anchorEl={helpAnchorEl}
              onClose={() => setHelpAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              disableRestoreFocus
              sx={{ pointerEvents: 'none' }}
            >
              <Box sx={{ p: 2, maxWidth: 360 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  What to enter
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Theme: a short visual category AI can rotate, such as Bedroom interior design, Festival offer banner, or Customer result.
                </Typography>
                <Typography variant="body2">
                  Image generation prompt: detailed visual direction including subject, style, materials, setting, lighting, brand mood, audience, location, and anything to avoid.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Theme color: the brand accent color AI should use in that theme's generated image. Website analysis can prefill it, and you can edit it here.
                </Typography>
              </Box>
            </Popover>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Prefill themes from the website and business summary, then edit the rotation AI should use.
          </Typography>
        </Box>
        <Stack direction="row" gap={1}>
          <Button variant="outlined" startIcon={<AutoAwesomeIcon />} onClick={onSuggest}>
            Suggest
          </Button>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={onAdd}>
            Add Theme
          </Button>
        </Stack>
      </Stack>
      {groupError && (
        <FormHelperText error sx={{ mb: 1 }}>
          {groupError}
        </FormHelperText>
      )}
      <Stack gap={1.5}>
        {asArray(values).map((item, index) => {
          const rowErrors = asArray(errors)[index] || {};
          return (
            <Grid container spacing={1.5} key={`image-theme-${index}`}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Theme"
                  value={item?.label || ''}
                  error={Boolean(rowErrors.label)}
                  helperText={rowErrors.label}
                  onChange={(e) => onChange(index, 'label', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  type="color"
                  label="Theme color"
                  value={normalizeThemeColorInput(item?.theme_color)}
                  helperText={item?.theme_color || 'Pick brand accent'}
                  onChange={(e) => onChange(index, 'theme_color', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Image generation prompt"
                  value={item?.prompt || ''}
                  error={Boolean(rowErrors.prompt)}
                  helperText={rowErrors.prompt}
                  onChange={(e) => onChange(index, 'prompt', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton onClick={() => onDelete(index)}>
                  <CloseIcon />
                </IconButton>
              </Grid>
            </Grid>
          );
        })}
      </Stack>
    </Grid>
  );
};

const ReferenceImageEditor = ({ values, errors = [], uploadingKey, uploadProgress, onAdd, onDelete, onChange, onUpload }) => (
  <Grid item xs={12}>
    <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={1} sx={{ mb: 1 }}>
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Reference images
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Upload images and describe what AI should learn from them for future generated visuals.
        </Typography>
      </Box>
      <Button variant="outlined" startIcon={<AddIcon />} onClick={onAdd}>
        Add Reference
      </Button>
    </Stack>
    <Stack gap={1.5}>
      {asArray(values).map((item, index) => {
        const rowErrors = asArray(errors)[index] || {};
        const previewUrl = asArray(item?.thumbnail_urls)[0] || item?.media_url;
        const isUploading = uploadingKey === `reference-${index}`;
        return (
          <Grid container spacing={1.5} key={`reference-image-${index}`} alignItems="stretch">
            <Grid item xs={12} md={3}>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, height: '100%', minHeight: 116 }}>
                {previewUrl ? (
                  <Box component="img" src={previewUrl} alt="Reference" sx={{ width: '100%', height: 72, objectFit: 'cover', borderRadius: 1, mb: 1 }} />
                ) : (
                  <Box sx={{ height: 72, borderRadius: 1, bgcolor: 'grey.100', mb: 1 }} />
                )}
                <Button component="label" fullWidth size="small" variant="outlined" startIcon={<CloudUploadIcon />}>
                  {isUploading ? `${uploadProgress || 1}%` : 'Upload'}
                  <input hidden accept=".jpg,.jpeg,.png,.webp" type="file" onChange={(e) => onUpload(index, e.target.files)} />
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                label="Reference direction"
                value={item?.description || ''}
                error={Boolean(rowErrors.description)}
                helperText={rowErrors.description || 'Example: use the lighting, wood tone, cabinet style, or layout from this image.'}
                onChange={(e) => onChange(index, 'description', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton onClick={() => onDelete(index)}>
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
        );
      })}
    </Stack>
  </Grid>
);

const LogoSettingsEditor = ({ value, errors = {}, uploadingKey, uploadProgress, onChange, onUpload }) => {
  const logoSettings = normalizeLogoSettings(value);
  const previewUrl = asArray(logoSettings.thumbnail_urls)[0] || logoSettings.media_url;
  const isUploading = uploadingKey === 'logo';

  return (
    <Grid item xs={12}>
      <Box sx={{ border: '1px solid', borderColor: errors.media_url || errors.position ? 'error.main' : 'divider', borderRadius: 1, p: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Business logo in generated images
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload a logo and choose where it should appear in generated visuals.
            </Typography>
          </Box>
          <FormControlLabel
            control={<Switch checked={logoSettings.use_logo} onChange={(e) => onChange({ use_logo: e.target.checked })} />}
            label="Add logo"
          />
        </Stack>
        {logoSettings.use_logo && (
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={3}>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                {previewUrl ? (
                  <Box component="img" src={previewUrl} alt="Logo" sx={{ width: '100%', height: 72, objectFit: 'contain', borderRadius: 1, mb: 1, bgcolor: 'grey.50' }} />
                ) : (
                  <Box sx={{ height: 72, borderRadius: 1, bgcolor: 'grey.100', mb: 1 }} />
                )}
                <Button component="label" fullWidth size="small" variant="outlined" startIcon={<CloudUploadIcon />}>
                  {isUploading ? `${uploadProgress || 1}%` : 'Upload Logo'}
                  <input hidden accept=".jpg,.jpeg,.png,.webp" type="file" onChange={(e) => onUpload(e.target.files)} />
                </Button>
                {errors.media_url && <FormHelperText error>{errors.media_url}</FormHelperText>}
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <FormControl fullWidth error={Boolean(errors.position)}>
                <InputLabel>Logo position</InputLabel>
                <Select
                  label="Logo position"
                  value={logoSettings.position}
                  onChange={(e) => onChange({ position: e.target.value })}
                >
                  {logoPositions.map((position) => (
                    <MenuItem key={position} value={position}>
                      {position}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{errors.position}</FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        )}
      </Box>
    </Grid>
  );
};

const ReviewRow = ({ label, value }) => (
  <>
    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
      <Typography color="text.secondary">{label}</Typography>
      <Typography sx={{ textAlign: 'right', fontWeight: 600 }}>{value}</Typography>
    </Stack>
    <Divider />
  </>
);

export default AIAutoPilotV2;
